import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

const COMPONENT_LABELS = {
  kills: 'Kills',
  damage: 'Damage',
  utility: 'Utility',
  deaths: 'Deaths',
  trades: 'Trades',
  objectives: 'Objectives',
};

function coerceDisplayNumber(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function getRatingColor(value) {
  if (value == null) return '#999999';
  if (value >= 1.30) return '#FFD700';
  if (value >= 1.10) return '#4CAF50';
  if (value >= 0.90) return '#999999';
  if (value >= 0.70) return '#FF9800';
  return '#F44336';
}

function getComponentColor(value) {
  if (value == null) return '#999999';
  if (value > 0) return '#4CAF50';
  if (value < 0) return '#F44336';
  return '#999999';
}

function ComponentBreakdown({ components }) {
  if (!components) return null;

  return (
    <Box sx={{ padding: '4px 0', minWidth: 140 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', marginBottom: '6px', color: '#ffffff' }}>
        Impact components
      </Typography>
      {Object.entries(COMPONENT_LABELS).map(([key, label]) => {
        const val = coerceDisplayNumber(components[key]);
        if (val == null) return null;
        return (
          <Box
            key={key}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '2px' }}
          >
            <Typography sx={{ fontSize: '0.7rem', color: '#cccccc' }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: getComponentColor(val) }}>
              {val >= 0 ? '+' : ''}{val.toFixed(2)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function ImpactRatingBadge({
  value,
  components,
  size = 'medium',
  showLabel = false,
  compact = false,
  valueStyle,
}) {
  const normalizedValue = coerceDisplayNumber(value);

  if (normalizedValue == null) {
    if (compact) return null;
    return (
      <Typography sx={{ fontSize: '0.85rem', color: '#999999' }}>-</Typography>
    );
  }

  const color = getRatingColor(normalizedValue);
  const fontSize = size === 'small' ? '0.85rem' : size === 'large' ? '1.3rem' : '1rem';
  const labelFontSize = size === 'small' ? '0.6rem' : '0.7rem';

  const badge = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
      {showLabel && (
        <Typography
          sx={{
            fontSize: labelFontSize,
            color: '#999999',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: 500,
          }}
        >
          IMP
        </Typography>
      )}
      <Typography
        style={valueStyle}
        sx={{
          fontSize,
          fontWeight: 700,
          color,
        }}
      >
        {normalizedValue.toFixed(2)}
      </Typography>
    </Box>
  );

  if (!components) return badge;

  return (
    <Tooltip
      title={<ComponentBreakdown components={components} />}
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#1C1E20',
            border: '1px solid #6c757d',
            borderRadius: '6px',
            padding: '0.5rem 0.75rem',
          },
        },
      }}
    >
      {badge}
    </Tooltip>
  );
}

ImpactRatingBadge.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  components: PropTypes.object,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showLabel: PropTypes.bool,
  compact: PropTypes.bool,
  valueStyle: PropTypes.object,
};
